import FirebaseAdapter from "@wc/adapters/firebase";
import {
  PermissionsEnum,
  userHasPermissionInOrg,
  userHasPermissionInTeam,
} from "@wc/domain/authz";
import { clearPrimaryTeamIfMatches } from "@wc/domain/team-membership";

/**
 * Delete a team (soft delete)
 * User must have DELETE_TEAM permission either at org level (ORG_ADMIN) or team level (TEAM_ADMIN)
 * @param {string} teamId The team to delete
 * @param {string} organizationId The org the team belongs to
 * @param {object} user The user performing the deletion
 * @returns {Promise<void>}
 */
export async function deleteTeam(teamId, organizationId, user) {
  // Check org-level permission first (ORG_ADMIN can delete any team)
  let canDelete = false;

  try {
    canDelete = await userHasPermissionInOrg(
      user,
      organizationId,
      PermissionsEnum.DELETE_TEAM,
    );
  } catch {
    // User might not have org role, check team level
  }

  // If not org-level, check team-level permission (TEAM_ADMIN can delete their team)
  if (!canDelete) {
    try {
      canDelete = await userHasPermissionInTeam(
        user,
        teamId,
        PermissionsEnum.DELETE_TEAM,
      );
    } catch {
      canDelete = false;
    }
  }

  if (!canDelete) {
    throw new Error("You do not have permission to delete this team");
  }

  // Get all team members and update their primary team if needed
  const teamMembers = await FirebaseAdapter().membership.listTeamMembers(
    teamId,
    { limit: 0 },
  );

  // Clear primary team for all members who have this team as primary
  await Promise.all(
    teamMembers.map((member) =>
      clearPrimaryTeamIfMatches(member.userId, organizationId, teamId),
    ),
  );

  await FirebaseAdapter().teams.deleteTeam(teamId, user.id);
}
